/**
 * Baseball's screen model: the cups, the standing distance markers and the
 * hit legend the phone and the TVs draw for a game state, free of React
 * Native so node tests (and the TV page's fixtures) can use it.
 *
 * Cole's multiplayer notes, 2026-10-18: a standing marker at each cup like
 * Football's, colored by the hit's tolerance stage; a used-up hit's cup and
 * ring come off the green (the ball rolls over the spot as plain green) while
 * and so does its standing marker (2026-10-19); the scoreboard's hit labels carry the same colors
 * instead of the ring-size text.
 */
import type { PuttSceneExtraCup } from '../../../../packages/putt-scene/src/cupMarkers';
import type { PuttSceneRolloutBand } from '../../../../packages/putt-scene/src/SceneContent';
import {
  BASEBALL_BUNT_HOLE_FT,
  BASEBALL_HIT_HOLES_FT,
  BASEBALL_RING_FT,
  BASEBALL_SAC_FLY_HOLD_FT,
  baseballBattingTeam,
  baseballBuntOn,
  baseballHitStage,
  baseballHitToleranceFt,
  baseballSacFlyBandFt,
  baseballSacFlyHolesFt,
  type BaseballHoleFt,
  type BaseballRead,
  type BaseballState,
} from './baseball';

/** Stage colors, one per tolerance stage: white = the full 3 ft ring, yellow =
 *  1.5 ft, red = hole it, gray = used up. The same four as Football's
 *  FOOTBALL_STAGE_MARKER_COLORS (footballView.ts). */
export const BASEBALL_STAGE_COLORS = ['#FFFFFF', '#E3B341', '#F0605D', '#8A94A0'] as const;
/** The same size as Football's markers (FOOTBALL_MARKER_LABEL_SCALE). */
export const BASEBALL_MARKER_LABEL_SCALE = 2.5;
const RING_COLOR = '#f5f8eb';
/** colors.gold (theme/tokens.ts, which imports React Native). */
const GOLD = '#C8922A';

/** A stage's color; anything out of range reads as the fresh 3 ft (white). */
export function baseballStageColor(stage: unknown): string {
  return typeof stage === 'number' && Number.isInteger(stage) && stage >= 0 && stage <= 3
    ? BASEBALL_STAGE_COLORS[stage]
    : BASEBALL_STAGE_COLORS[0];
}

/**
 * The cups on the green. Batting: each hit hole still available, ringed at
 * the batting team's current tolerance (3 ft, then 1.5 ft; a make-only hole
 * has no ring; Cole, 2026-10-16), plus the bunt cup when a bunt is on. A
 * used-up hit has no cup and no ring (Cole, 2026-10-18). Fielding: the
 * batter's hole lit, with a gold ring at the batter's leave to get inside;
 * the other available holes stay drawn, dimmed.
 */
export function baseballExtraCups(state: BaseballState): PuttSceneExtraCup[] {
  const pending = state.phase === 'fielding' ? state.pending : null;
  const batting = baseballBattingTeam(state);
  // A double-play chance is one putt at one hole: the others come off the
  // green so nothing else looks like it is in play (Cole, 2026-10-08).
  if (pending && pending.kind !== 'hit') {
    // A sacrifice fly's target is the 1.5-ft ring (2026-10-16); a double-play
    // ball still has to be holed, so its hole keeps the normal ring.
    const ringRadiusFt = pending.kind === 'sacFly' ? BASEBALL_SAC_FLY_HOLD_FT : BASEBALL_RING_FT;
    return [{ id: `hole-${pending.hole}`, distanceFt: pending.hole, ringRadiusFt, color: GOLD, opacity: 0.9, lit: true }];
  }
  const cups: PuttSceneExtraCup[] = [];
  for (const hole of BASEBALL_HIT_HOLES_FT) {
    const stage = baseballHitStage(state, batting, hole);
    // Used up: off the green; the ball rolls over the spot (2026-10-18).
    if (stage >= 3 && pending?.hole !== hole) continue;
    const tolerance = baseballHitToleranceFt(stage);
    cups.push({
      id: `hole-${hole}`,
      distanceFt: hole,
      ...(tolerance ? { ringRadiusFt: tolerance } : {}),
      color: RING_COLOR,
      opacity: pending && pending.hole !== hole ? 0.28 : 0.75,
      lit: pending?.hole === hole,
    });
  }
  if (pending?.kind === 'hit') {
    cups.push({ id: 'leave', distanceFt: pending.hole, ringRadiusFt: pending.leaveFt, color: GOLD, opacity: 0.95 });
  }
  if (baseballBuntOn(state)) cups.push({ id: 'bunt', distanceFt: BASEBALL_BUNT_HOLE_FT });
  // A sacrifice fly is on: a dashed ring at the outer edge of each hole's
  // 2-ft sac-fly band (5 ft, then 3.5, then 2 at make-only).
  for (const hole of baseballSacFlyHolesFt(state)) {
    const band = baseballSacFlyBandFt(baseballHitStage(state, batting, hole));
    if (!band) continue;
    cups.push({ id: `sacfly-${hole}`, distanceFt: hole, ringRadiusFt: band.maxFt, color: GOLD, opacity: 0.9, dashed: true, ringOnly: true });
  }
  return cups;
}

/** The distances with a cup on the green: only these can take the ball. */
export function baseballCupsOnGreenFt(state: BaseballState): BaseballHoleFt[] {
  const holes: BaseballHoleFt[] = [];
  for (const cup of baseballExtraCups(state)) {
    if (cup.ringOnly || cup.id === 'leave') continue;
    if (!holes.includes(cup.distanceFt as BaseballHoleFt)) holes.push(cup.distanceFt as BaseballHoleFt);
  }
  return holes;
}

/**
 * The cup this putt dropped into, so the scene sinks the ball there; null
 * when it dropped into none. A read that says holed at a spot with no cup
 * (a used-up hit, the bunt cup while no bunt is on) is a ball rolling over
 * plain green.
 */
export function baseballHoledCupFt(state: BaseballState, read: BaseballRead): BaseballHoleFt | null {
  const onGreen = baseballCupsOnGreenFt(state);
  if (state.phase === 'fielding' && state.pending) {
    return read[state.pending.hole].holed ? state.pending.hole : null;
  }
  return BASEBALL_HIT_HOLES_FT.find((hole) => onGreen.includes(hole) && read[hole].holed)
    ?? (onGreen.includes(BASEBALL_BUNT_HOLE_FT) && read[BASEBALL_BUNT_HOLE_FT].holed ? BASEBALL_BUNT_HOLE_FT : null);
}

/**
 * Standing distance markers level with each cup, up the left side, like
 * Football's (Cole, 2026-10-18): the number alone, colored by the batting
 * team's stage for that hit (white 3 ft, yellow 1.5 ft, red hole it). A
 * used-up hit has no marker at all (Cole, 2026-10-19: "when you can no
 * longer make for a given distance, don't even render the number"). The
 * bunt cup's marker stands while a bunt is on. While the fielder putts there
 * are none at all: the fielder's distance is big in the lower-right corner
 * instead, like Football's defense (Cole, 2026-10-19).
 */
export function baseballDistanceMarkers(state: BaseballState): PuttSceneRolloutBand[] {
  if (state.phase === 'complete' || state.phase === 'fielding') return [];
  const batting = baseballBattingTeam(state);
  const marker = (hole: BaseballHoleFt, color: string): PuttSceneRolloutBand => ({
    id: `marker-${hole}`,
    minFt: hole,
    maxFt: hole,
    label: String(hole),
    labelOrientation: 'billboard',
    lineColor: color,
    labelOnly: true,
    labelScale: BASEBALL_MARKER_LABEL_SCALE,
  });
  const markers = BASEBALL_HIT_HOLES_FT
    .filter((hole) => baseballHitStage(state, batting, hole) < 3)
    .map((hole) => marker(hole, baseballStageColor(baseballHitStage(state, batting, hole))));
  if (baseballBuntOn(state)) markers.unshift(marker(BASEBALL_BUNT_HOLE_FT, BASEBALL_STAGE_COLORS[0]));
  return markers;
}

/**
 * How the progressive tolerance resets, for the scoreboards (Cole,
 * 2026-10-19: "It needs to be a little more clear that you need to make all to
 * reset the rings"): "HIT ALL 4 TO RESET · 2 LEFT" while some hit has
 * tightened, the number being the hits the team still has to earn (a stage
 * above 0 means earned since the last reset). Null while every hit is fresh
 * or the stages are unknown.
 */
export function baseballResetLine(stages: readonly unknown[] | null | undefined): string | null {
  if (!Array.isArray(stages) || stages.length !== 4) return null;
  const left = stages.filter((stage) => stage === 0).length;
  return left === 0 || left === 4 ? null : `HIT ALL 4 TO RESET · ${left} LEFT`;
}

/** What the TV's PICK UP YOUR PHONE prompt says while the tied game waits on the extra-innings choice. */
export const BASEBALL_EXTRA_CHOICE_PHONE_ACTION = 'choose one at-bat each or another inning';
